import React from "react";
import { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import DialogContent from "@mui/material/DialogContent";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import InsightsIcon from "@mui/icons-material/Insights";
import MyChart from "./MyChart.js";

import { generateDashboard } from "../utils/AwsCalls.js";
import MyLoader from "./MyLoader";
import MarkdownRenderer from "./MarkdownRenderer.js";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function DashboardDialog(props) {
  const { answers, handleClose, open } = props;
  const [dataDashboard, setDataDashboard] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  const effectRan = React.useRef(false);
  useEffect(() => {
    if (!effectRan.current) {
      console.log("effect applied - only on the FIRST mount");

      const fetchData = async () => {
        console.log("Dashboard...");
        console.log(answers);

        let questions_and_answers = [];
        let data_charts = [];

        for (let i = 0; i < answers.length; i++) {
          const answer = answers[i];
          if (
            answer.hasOwnProperty("data_result_uuids") &&
            answer.data_result_uuids !== ""
          ) {
            questions_and_answers.push({
              question: answers[i - 1].query,
              answer: answer.text,
            });
          }
          if (answer.hasOwnProperty("chart")) {
            data_charts.push({
              caption: answer.chart.caption,
            });
          }
        }
        console.log(questions_and_answers);
        console.log(data_charts);

        // Call the utility function instead of using the local one
        const result = await generateDashboard(
          questions_and_answers,
          data_charts
        );
        console.log(result);
        setDataDashboard(result);
        setLoading(false);
      };
      fetchData()
        // catch any error
        .catch(console.error);
    }
    return () => (effectRan.current = true);
  }, [answers]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar
        sx={(theme) => ({
          position: "fixed",
          backdropFilter: "blur(20px)",
          boxShadow: "0 4px 30px rgba(255, 255, 255, 0.3)",
          borderBottom: "1px solid",
          borderColor: "rgba(255, 255, 255, 0.4)",
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 100%)",
        })}
        color="default"
        elevation={0}
      >
        <Toolbar sx={{ p: 1, m: 0 }}>
          <InsightsIcon color="primary" />
          <Typography
            color="text.primary"
            sx={{ ml: 2, flex: 1 }}
            variant="h6"
            component="div"
          >
            Business Intelligence Report
          </Typography>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon color="secondary" />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent
        sx={{
          minHeight: "100vh",
          backgroundColor: "#FFF",
          backgroundImage: `
      /* Top fade overlay - white with opacity transition */
      linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5) 40%, rgba(255, 255, 255, 0.95)),
      /* Middle to bottom white overlay */
      linear-gradient(180deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.75), rgba(255, 255, 255, 1) 30%),
      /* Subtle white highlight */
      radial-gradient(circle at 70% 20%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 40%),
      /* Subtle white highlight */
      radial-gradient(circle at 30% 80%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 40%),
      /* Main color gradient */
      conic-gradient(from 180deg at 50% 100%, #62ff57 -144deg, #f299ff 144deg, #b2a8ff 162deg 179.91deg, #75cfff 198deg, #62ff57 216deg, #f299ff 504deg)
    `,
          backgroundSize:
            "100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment:
            "fixed" /* Ensures the background stays fixed relative to the viewport */,
          pt: 10, // Preserving the original padding top value
        }}
      >
        {loading && (
          <MyLoader loading={loading} message="Generating your BI Report..." />
        )}

        {dataDashboard.title !== undefined && (
          <Box>
            <Container maxWidth="100%" disableGutters>
              <Grow timeout={1500} in={true}>
                <Box>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                      fontWeight: 500,
                      fontSize: { lg: "2.125rem", xl: "2.125rem" },
                    }}
                    gutterBottom
                  >
                    {dataDashboard.title}
                  </Typography>

                  <Typography
                    component="div"
                    color="primary"
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      fontSize: { lg: "1.3rem", xl: "1.3rem" },
                    }}
                    gutterBottom
                  >
                    <MarkdownRenderer
                      content={dataDashboard.executive_summary}
                    />
                  </Typography>

                  <Divider sx={{ mt: 2, mb: 2, opacity: 0.5 }} />

                  <Typography
                    component="div"
                    sx={{ fontSize: { lg: "1.225rem", xl: "1.225rem" } }}
                    gutterBottom
                  >
                    <MarkdownRenderer content={dataDashboard.data_analysis} />
                  </Typography>
                </Box>
              </Grow>

              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                sx={{ mb: 2, mt: 2 }}
              >
                {answers.map(
                  (answer, index) =>
                    answer.hasOwnProperty("chart") &&
                    answer.chart.hasOwnProperty("chart_type") && (
                      <Grid size={{ sm: 12, md: 6, lg: 6, xl: 6 }} key={index}>
                        <Box
                          id={"chart" + index}
                          sx={(theme) => ({
                            m: 0,
                            borderRadius: 4,
                            bgcolor: "rgba(248, 255, 252, 0.1)",
                          })}
                        >
                          <MyChart
                            caption={answer.chart.caption}
                            options={answer.chart.chart_configuration.options}
                            series={answer.chart.chart_configuration.series}
                            type={answer.chart.chart_type}
                          />
                        </Box>
                      </Grid>
                    )
                )}
              </Grid>

              <Grow timeout={1500} in={true}>
                <Box>
                  <Typography
                    color="primary"
                    sx={{
                      fontWeight: 500,
                      fontSize: { lg: "1.125rem", xl: "1.225rem" },
                    }}
                    gutterBottom
                  >
                    Conclusion
                  </Typography>

                  <Typography
                    component="div"
                    sx={{ fontSize: { lg: "1.225rem", xl: "1.225rem" } }}
                    gutterBottom
                  >
                    <MarkdownRenderer content={dataDashboard.conclusion} />
                  </Typography>
                </Box>
              </Grow>
            </Container>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DashboardDialog;
